import { IconButton, ReferenceArea } from '@/components/admin/reglages/reference-list';
import { Callout } from '@/components/ui/callout';
import { RequiredMark } from '@/components/ui/required-mark';
import {
  GUARDRAIL_ACTION_HINT,
  GUARDRAIL_ACTION_LABEL,
  consecutiveTurnsSentence,
} from '@/lib/rules-labels';
import type { GuardrailRow } from '@/lib/rules-settings';
import {
  actionOfKind,
  directionOf,
  guardrailBlocked,
  newGuardrailRow,
  parseConsecutiveTurns,
} from '@/lib/rules-settings';
import type { GuardrailActionKind } from '@matter_extractor/shared';
import {
  GUARDRAIL_CONSECUTIVE_TURNS_MAX,
  GUARDRAIL_DETECTION_MAX,
  GUARDRAIL_DIRECTION_MAX,
  guardrailActionKindSchema,
} from '@matter_extractor/shared';
import { Plus, Trash2 } from 'lucide-react';
import { useId, useRef, useState } from 'react';

/**
 * Section 2 of Réglages > Règles: the guardrails (§8.5, §11.3).
 *
 * What the scribe must detect on a turn, for how many consecutive turns, and
 * what the engine does about it. Evaluated BEFORE everything else, and the
 * interviewer never learns they exist - it only receives the direction one of
 * them injects.
 *
 * ## It holds NO draft: the draft of « Les règles » is the layer's
 *
 * Since issue #192 (vitrine.md section 5) the guardrails travel WITH the rules in
 * one versioned object, so this list is a FORM over the rows of ITS draft - read
 * from its props, every change handed back whole. The save of one guardrail
 * alone, its refusal under its own row and its confirmation of a deletion went
 * with the writes they belonged to: the layer autosaves the draft, and « Enregistrer
 * une nouvelle version » is the one write. A guardrail still has NO order -
 * §8.5 says « un garde-fou déclenché l'emporte » and nothing about which of two
 * fires first - and the list keeps its reading order.
 *
 * ## The direction survives a switch of the action
 *
 * The draft's action is the union of §8.5, where only « injecter une direction »
 * holds a text. An operator who picks it, types four lines, then switches to
 * « signaler seulement » and back must find their four lines again: the list
 * REMEMBERS the text typed on each row for the life of the page - in a ref, since
 * it never draws anything - and puts it back when the action returns.
 *
 * A version opened by « Voir » is `readOnly`: the whole section is `inert`.
 */

const FIELD_BOX =
  'rounded-md border-2 border-gray-300 bg-white focus-visible:border-bleu-nuit focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1';

export interface GuardrailListProps {
  /** The guardrails of the draft, in their reading order. */
  readonly guardrails: readonly GuardrailRow[];
  /** A version opened by « Voir », or a retired entity: nothing is typable. */
  readonly readOnly: boolean;
  /** The next list, whole: a rewrite, an addition or a removal. */
  readonly onChange: (rows: GuardrailRow[]) => void;
}

export function GuardrailList({ guardrails: rows, readOnly, onChange }: GuardrailListProps) {
  const headingId = useId();
  /** The direction last typed on each row, by id, to put back when its action returns. */
  const remembered = useRef(new Map<string, string>());

  function patch(id: string, over: Partial<GuardrailRow>): void {
    onChange(rows.map((row) => (row.id === id ? { ...row, ...over } : row)));
  }

  function setAction(row: GuardrailRow, kind: GuardrailActionKind): void {
    // Written down BEFORE the action changes: the draft has no place for the
    // text on another action, so this is the only copy that survives the switch.
    remembered.current.set(row.id, directionOf(row) || (remembered.current.get(row.id) ?? ''));
    patch(row.id, { action: actionOfKind(kind, remembered.current.get(row.id) ?? '') });
  }

  function setDirection(row: GuardrailRow, text: string): void {
    remembered.current.set(row.id, text);
    patch(row.id, { action: actionOfKind('inject-direction', text) });
  }

  return (
    <section
      aria-labelledby={headingId}
      data-testid="reglages-garde-fous"
      className="space-y-4"
      {...(readOnly ? { inert: true } : {})}
    >
      <header className="space-y-1">
        <h3 id={headingId} className="font-semibold text-bleu-nuit text-lg leading-7">
          Garde-fous
        </h3>
        <p className="max-w-3xl text-gray-700 text-sm">
          Le scribe rend vrai ou faux par garde-fou à chaque tour, le moteur tient la série. Ils
          sont évalués avant tout le reste : un garde-fou déclenché l’emporte. L’interviewer ne sait
          pas qu’ils existent - il ne reçoit que la direction injectée, le cas échéant.
        </p>
      </header>

      <ol className="space-y-4">
        {rows.map((row, index) => {
          const blocked = guardrailBlocked(row);
          return (
            <li
              key={row.id}
              data-testid="reglages-garde-fou"
              className="space-y-3 rounded-md border-2 border-gray-200 p-4"
            >
              <div className="flex items-start justify-between gap-2">
                <h4 className="font-semibold text-bleu-nuit text-sm">Garde-fou {index + 1}</h4>
                <IconButton
                  label={`Supprimer le garde-fou ${index + 1}`}
                  tone="danger"
                  testId="reglages-garde-fou-supprimer"
                  onClick={() => onChange(rows.filter((one) => one.id !== row.id))}
                >
                  <Trash2 size={16} strokeWidth={2} aria-hidden="true" />
                </IconButton>
              </div>

              <ReferenceArea
                label="Ce que le scribe doit détecter sur le tour"
                testId="reglages-garde-fou-detection"
                value={row.detection}
                max={GUARDRAIL_DETECTION_MAX}
                placeholder="la personne n’a pas répondu du tout à la question"
                onChange={(detection) => patch(row.id, { detection })}
              />

              <div className="grid gap-3 md:grid-cols-2">
                <TurnsField
                  value={row.consecutiveTurns}
                  onChange={(consecutiveTurns) => patch(row.id, { consecutiveTurns })}
                />
                <ActionField value={row.action.kind} onChange={(kind) => setAction(row, kind)} />
              </div>

              {row.action.kind === 'inject-direction' ? (
                <ReferenceArea
                  label="La direction injectée à l’interviewer"
                  testId="reglages-garde-fou-direction"
                  value={row.action.text}
                  max={GUARDRAIL_DIRECTION_MAX}
                  placeholder="sors du rôle en une phrase, rappelle le cadre donné au début"
                  onChange={(text) => setDirection(row, text)}
                />
              ) : null}

              {blocked === null ? null : (
                <p data-testid="reglages-garde-fou-bloque" className="text-gray-700 text-sm">
                  {blocked}
                </p>
              )}
            </li>
          );
        })}
      </ol>

      {rows.length === 0 ? (
        <p className="text-gray-600 text-sm">
          Aucun garde-fou : rien n’est surveillé en dehors des conditions de sortie des étapes.
        </p>
      ) : null}

      <button
        type="button"
        data-testid="reglages-garde-fou-ajouter"
        onClick={() => onChange([...rows, newGuardrailRow()])}
        className="flex h-13 w-full cursor-pointer items-center justify-center gap-2 rounded-md border-2 border-gray-300 border-dashed bg-white font-semibold text-base text-bleu-nuit hover:bg-gray-50"
      >
        <Plus size={18} strokeWidth={2} aria-hidden="true" />
        Ajouter un garde-fou
      </button>

      <Callout tone="neutral">
        <p>
          Les garde-fous partent avec les règles, dans la même version : retirer un garde-fou le
          sort de la prochaine version, l’historique garde celle qui l’avait, et les sessions déjà
          commencées gardent la version qu’elles ont figée. « Fermer l’expérience » utilise le texte
          de clôture par défaut, qui se règle sur l’étape et pas ici.
        </p>
      </Callout>
    </section>
  );
}

/**
 * « pendant N tours consécutifs » (§8.5), with what N means underneath.
 *
 * The field keeps what was TYPED and the draft only moves on a value that parses
 * (`parseConsecutiveTurns`): a number field being edited passes through the empty
 * string, and a draft typed as a number would either refuse the keystroke or
 * silently read it as zero. A typed value the draft cannot hold is said under
 * the field, and the draft keeps the last one it could.
 */
function TurnsField({
  value,
  onChange,
}: {
  value: number;
  onChange: (next: number) => void;
}) {
  const id = useId();
  const [typed, setTyped] = useState<string | null>(null);
  // What the field shows: what is being typed, or the draft. They rejoin as soon
  // as the value parses, so a draft moved from elsewhere - an undo, a reload -
  // is never hidden behind a stale keystroke.
  const shown = typed !== null && parseConsecutiveTurns(typed) === null ? typed : String(value);
  const refused = typed !== null && parseConsecutiveTurns(typed) === null;
  return (
    <div className="space-y-1">
      <label htmlFor={id} className="block font-semibold text-gray-800 text-xs">
        Pendant N tours consécutifs
        <RequiredMark />
      </label>
      <input
        id={id}
        type="number"
        inputMode="numeric"
        min={1}
        max={GUARDRAIL_CONSECUTIVE_TURNS_MAX}
        data-testid="reglages-garde-fou-tours"
        value={shown}
        required
        aria-required="true"
        onChange={(event) => {
          const next = event.currentTarget.value;
          const turns = parseConsecutiveTurns(next);
          setTyped(turns === null ? next : null);
          if (turns !== null) onChange(turns);
        }}
        className={`${FIELD_BOX} h-10 w-24 px-3 text-sm`}
      />
      <p className="text-gray-500 text-xs leading-4">
        {refused
          ? `Le nombre de tours consécutifs est un entier, de 1 à ${GUARDRAIL_CONSECUTIVE_TURNS_MAX}.`
          : consecutiveTurnsSentence(value)}
      </p>
    </div>
  );
}

/**
 * The closed list of five actions (§8.5), with what each one does underneath.
 *
 * The order is the SCHEMA's (`guardrailActionKindSchema`), never re-sorted
 * here: « Conclure par l'adieu » (issue #163) is third, right after the two
 * closings it is a gentler form of, and a list sorted by label would scatter
 * the three actions that end something.
 */
function ActionField({
  value,
  onChange,
}: {
  value: GuardrailActionKind;
  onChange: (next: GuardrailActionKind) => void;
}) {
  const id = useId();
  return (
    <div className="space-y-1">
      <label htmlFor={id} className="block font-semibold text-gray-800 text-xs">
        Action
        <RequiredMark />
      </label>
      <select
        id={id}
        data-testid="reglages-garde-fou-action"
        value={value}
        onChange={(event) => {
          // The value is read HERE and not inside an updater: React nulls
          // `currentTarget` the moment the handler returns, which took the
          // sharing form down once (`link-form.tsx`).
          const next = event.currentTarget.value as GuardrailActionKind;
          onChange(next);
        }}
        className={`${FIELD_BOX} h-10 w-full px-3 text-sm`}
      >
        {guardrailActionKindSchema.options.map((kind) => (
          <option key={kind} value={kind}>
            {GUARDRAIL_ACTION_LABEL[kind]}
          </option>
        ))}
      </select>
      <p className="text-gray-500 text-xs leading-4">{GUARDRAIL_ACTION_HINT[value]}</p>
    </div>
  );
}
